import { z } from "zod";
import { UserIdSchema } from "@visioni/a3s-users";

/**
 * @module preferences/types
 * Self-service notification preference types.
 */

/**
 * Notification channels a user can enable independently per category.
 *
 * These always belong to a real account holder, unlike the legacy per-camera
 * `EmailContact`/`SmsContact` lists in `a3s-alarm-schedules`, which hold
 * arbitrary unauthenticated addresses.
 */
export const NotificationChannel = z.enum(["email", "sms", "push"]);
export type NotificationChannel = z.infer<typeof NotificationChannel>;

export const NotificationChannelPreferences = z.object({
  email: z.boolean(),
  sms: z.boolean(),
  push: z.boolean(),
});
export type NotificationChannelPreferences = z.infer<typeof NotificationChannelPreferences>;

/**
 * The two things a user can be notified about. Values map to
 * `notification_preferences.notification_type`, which spells them in snake_case
 * (`alarm_event`/`camera_config_change`).
 */
export const NotificationCategory = z.enum(["alarmEvent", "cameraConfigChange"]);
export type NotificationCategory = z.infer<typeof NotificationCategory>;

/**
 * The two things a user can be notified about, each with its own independent set of
 * channels — one `NotificationChannelPreferences` per {@link NotificationCategory}.
 */
export const NotificationCategoryPreferences = z.object({
  alarmEvent: NotificationChannelPreferences,
  cameraConfigChange: NotificationChannelPreferences,
});
export type NotificationCategoryPreferences = z.infer<typeof NotificationCategoryPreferences>;

/** All channels off — the implicit value for a scope+category with no saved row yet. */
export const NO_NOTIFICATION_CHANNELS: NotificationChannelPreferences = { email: false, sms: false, push: false };
/** Both categories, all channels off — the implicit value for a scope with no saved row yet. */
export const DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES: NotificationCategoryPreferences = {
  alarmEvent: NO_NOTIFICATION_CHANNELS,
  cameraConfigChange: NO_NOTIFICATION_CHANNELS,
};

/**
 * Which cameras a `NotificationPreference` row applies to. `site`/`allCameras` are
 * wildcards — a `site` row matches every camera at that site (present or future), and
 * `allCameras` matches every camera the user can see across every site, rather than
 * enumerating cameras individually. `camera` matches exactly one. No scope here
 * implies any other — a user with a site override still needs a separate `allCameras`
 * row for their global default, and a `camera` row doesn't inherit from its site's
 * wildcard; resolving a camera's *effective* preference means checking for a `camera`
 * row first, then that camera's `site` wildcard, then `allCameras` — that resolution
 * is a query concern, not modelled by this type itself. "The user can see" is
 * deliberately not enforced when a preference is saved (the user's access can change
 * afterwards) — it's checked fresh at dispatch time instead, against whatever access
 * the user actually has when a notification is about to go out.
 *
 * Maps to `notification_preferences`' `site_id`/`asset_id`/`camera_id` columns, which
 * use `''` (not NULL) as the wildcard/"unscoped" sentinel — see that table's column
 * comments for why. Row → scope: all three empty is `allCameras`, site_id alone is
 * `site`, all three set is `camera`. `siteId`/`assetId`/`cameraId` below reject the
 * empty string for exactly that reason — a `site`/`camera` scope built with an empty
 * id would write the same all-empty row as `allCameras` and silently collide with it.
 */
const NonEmptyId = z.string().min(1, "Must not be empty — an empty id collides with the wildcard sentinel.");

export const NotificationPreferenceScope = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("allCameras") }),
  z.object({ kind: z.literal("site"), siteId: NonEmptyId }),
  z.object({ kind: z.literal("camera"), siteId: NonEmptyId, assetId: NonEmptyId, cameraId: NonEmptyId }),
]);
export type NotificationPreferenceScope = z.infer<typeof NotificationPreferenceScope>;

/**
 * One account holder's notification settings for one scope (see
 * `NotificationPreferenceScope`). Always tied to a real a3s/Auth0 user — unlike the
 * legacy per-camera contacts, self-service notifications require an account.
 */
export const NotificationPreference = z.object({
  tenantId: z.string(),
  userId: UserIdSchema,
  scope: NotificationPreferenceScope,
  preferences: NotificationCategoryPreferences,
});
export type NotificationPreference = z.infer<typeof NotificationPreference>;
